import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import MultiBiomarkerProfile from './MultiBiomarkerProfile';
import BloodTestIntegration, { MicrobiologyDxTab } from './BloodTestIntegration';
import PatientAttachmentsView from '../../components/PatientAttachmentsView';
import { UserSelectors } from '../../reducers/user';

const Page = styled.div`
  padding: 24px;
  width: 100%;
  max-width: min(1920px, 100%);
  margin: 0 auto;
  box-sizing: border-box;
`;

const PageTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: 600;
  color: #111827;
  margin: 0 0 20px 0;
`;

const TabBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 20px;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 2px;
`;

const Tab = styled.button<{ $active: boolean }>`
  padding: 10px 14px;
  font-size: 0.8125rem;
  font-weight: 600;
  border: 1px solid ${(p) => (p.$active ? '#3b82f6' : 'transparent')};
  border-bottom: none;
  border-radius: 10px 10px 0 0;
  background: ${(p) => (p.$active ? '#eff6ff' : 'transparent')};
  color: ${(p) => (p.$active ? '#1d4ed8' : '#64748b')};
  cursor: pointer;
  margin-bottom: -2px;
  &:hover {
    color: #1e40af;
    background: ${(p) => (p.$active ? '#eff6ff' : '#f8fafc')};
  }
`;

const Placeholder = styled.div`
  padding: 48px 24px;
  text-align: center;
  color: #64748b;
  font-size: 0.9375rem;
  background: #f9fafb;
  border-radius: 12px;
  border: 1px dashed #e5e7eb;
`;

const PdfBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const PdfFrame = styled.div`
  width: min(960px, 96vw);
  height: min(88vh, 900px);
  background: #0f172a;
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.35);
`;

const PdfIframe = styled.iframe`
  flex: 1;
  width: 100%;
  border: none;
  background: #fff;
`;

const PdfClose = styled.button`
  flex-shrink: 0;
  padding: 14px 24px;
  font-size: 0.9375rem;
  font-weight: 600;
  color: #e2e8f0;
  background: #1e293b;
  border: none;
  cursor: pointer;
  &:hover {
    background: #334155;
  }
`;

const TABS = [
  { id: 'profile', label: 'Multi-biomarker Profile' },
  { id: 'blood', label: 'Blood Test' },
  { id: 'micro', label: 'Microbiology Test' },
  { id: 'molecular', label: 'Molecular Test' },
  { id: 'immune', label: 'Immune System Test' },
] as const;

type TabId = (typeof TABS)[number]['id'];

const BiomarkerCenterPage: React.FC = () => {
  const isPatientSession = useSelector(UserSelectors().isPatientSession);
  const [tab, setTab] = useState<TabId>('profile');
  /** Blob URL from fetchBiomarkerPdfBlobUrl (Django streams S3; not raw presigned URL). */
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
  }, [pdfUrl]);

  const onOpenPdf = useCallback((blobUrl: string) => {
    setPdfUrl(blobUrl);
  }, []);

  const closePdf = useCallback(() => setPdfUrl(null), []);

  if (isPatientSession) {
    return (
      <Page>
        <PageTitle>Biomarker Profile</PageTitle>
        <PatientAttachmentsView title="My Lab Records" />
      </Page>
    );
  }

  return (
    <Page>
      <PageTitle>Biomarker Profile</PageTitle>

      <TabBar role="tablist" aria-label="Biomarker Profile sections">
        {TABS.map((t) => (
          <Tab
            key={t.id}
            type="button"
            role="tab"
            $active={tab === t.id}
            aria-selected={tab === t.id}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </Tab>
        ))}
      </TabBar>

      {tab === 'profile' && <MultiBiomarkerProfile onOpenPdf={onOpenPdf} />}

      {tab === 'blood' && (
        <BloodTestIntegration />
      )}
      {tab === 'micro' && <MicrobiologyDxTab />}
      {tab === 'molecular' && (
        <Placeholder>Molecular Test — detailed views coming soon.</Placeholder>
      )}
      {tab === 'immune' && (
        <Placeholder>Immune System Test — detailed views coming soon.</Placeholder>
      )}
      {pdfUrl && (
        <PdfBackdrop role="dialog" aria-modal aria-label="PDF preview">
          <PdfFrame>
            <PdfIframe title="Clinical PDF" src={pdfUrl} />
            <PdfClose type="button" onClick={closePdf}>
              Close
            </PdfClose>
          </PdfFrame>
        </PdfBackdrop>
      )}
    </Page>
  );
};

export default BiomarkerCenterPage;
